// White-Label Branding System - SaaS + Marketplace Architecture
// Two distinct branding scopes: Platform (hostname) + Company (database)

/**
 * PLATFORM BRANDING (Marketplace/Public - No Company Context)
 * Used ONLY when no company context exists
 * - Marketplace browsing
 * - Public pages  
 * - Pre-company signup
 * 
 * NEVER stored in database, users, or persisted as identity
 */

export function resolveBrandFromHostname() {
  if (typeof window === 'undefined') return 'tradesmate'; // SSR fallback
  
  const hostname = window.location.hostname.toLowerCase();
  
  if (hostname.includes('smbmate')) {
    return 'smbmate';
  }
  
  // Default to tradesmate (includes tradesmatepro.com and localhost)
  return 'tradesmate';
}

/**
 * BRAND CONFIGURATION
 * Maps internal brand codes to display properties
 */

/**
 * BRAND VOCABULARY
 *
 * White-labelling covered identity (name, colour, domain) but never VOCABULARY, which
 * is why SMBMate still reads as a trade product: the nouns are hardcoded English in the
 * JSX. An accounting firm is shown "Technicians" and billed for a "Crew" seat.
 *
 * DELIBERATELY SMALL. This is not a global find-and-replace of every trade-ish word —
 * that was considered and rejected. Most of the app's vocabulary is ALREADY neutral:
 * "job", "customer", "quote", "invoice" and "schedule" are exactly what an accountant,
 * a salon and a plumber all say, and swapping them would make the copy worse for
 * everyone. Only terms that are genuinely trade-specific belong here.
 *
 * NOT IN THIS MAP, on purpose:
 *  - "Contractor" in the MARKETPLACE. That is the product model (customers post
 *    requests, contractors bid, only the winner pays a fee — CLAUDE.md §7), not a
 *    label. Renaming it changes what the two-sided market IS, which is an operator
 *    decision, not a copy pass.
 *  - Database enums, column names and internal identifiers. `work_orders`,
 *    `assigned_technician_id` and `lead_technician` stay exactly as they are; this is
 *    a presentation layer and nothing here reaches a query.
 *
 * Keys are singular/plural pairs because English needs both and template-time
 * pluralisation of a swapped noun is how you get "Team members's".
 */
const TRADE_TERMS = {
  worker: 'Technician',
  workers: 'Technicians',
  workerLower: 'technician',
  workersLower: 'technicians',
  crew: 'Crew',
  dispatch: 'Dispatch',
  fieldWork: 'Field Work'
};

const BUSINESS_TERMS = {
  // "Team member" rather than "Employee": it covers a contractor-of-one's helper and a
  // salon's stylist without implying a payroll relationship the app does not model.
  worker: 'Team member',
  workers: 'Team members',
  workerLower: 'team member',
  workersLower: 'team members',
  crew: 'Team',
  // "Dispatch" means sending someone to a site. A bookkeeper assigns work; nobody is
  // dispatched anywhere.
  dispatch: 'Scheduling',
  fieldWork: 'Work'
};

export const BRAND_CONFIG = {
  tradesmate: {
    code: 'tradesmate',
    name: 'TradesMatePro',
    domain: 'www.tradesmatepro.com',
    primaryColor: '#2563EB',
    secondaryColor: '#1E40AF',
    logo: '/assets/tradesmatepro-logo.png',
    favicon: '/assets/tradesmatepro-favicon.ico',
    supportEmail: 'jerry@tradesmatepro.com',
    salesEmail: 'jerry@tradesmatepro.com',
    tagline: 'Professional Trade Management Software',
    description: 'Complete business management solution for trade professionals',
    // Tailwind gradient classes for public marketplace headers. These are class NAMES,
    // not hex, so they must be literal strings — Tailwind cannot see interpolated values
    // at build time and would purge them.
    gradient: 'from-blue-600 to-indigo-700',
    terms: TRADE_TERMS
  },
  smbmate: {
    code: 'smbmate',
    // 'SMBMate' — matching whitelabels.display_name and the customer portal. This read
    // 'SmbMate' here while the DB, the portal and the signup flow all said 'SMBMate',
    // so the product's own name rendered two different ways depending on the screen.
    name: 'SMBMate',
    domain: 'www.smbmate.com', 
    primaryColor: '#0D9488',
    secondaryColor: '#115E59',
    logo: '/assets/smbmate-logo.png',
    favicon: '/assets/smbmate-favicon.ico',
    supportEmail: 'jerry@tradesmatepro.com',
    salesEmail: 'jerry@tradesmatepro.com',
    tagline: 'Small Business Management Made Easy',
    description: 'Streamlined business tools for small business owners',
    // Teal, to match this brand's primaryColor above rather than TradesMatePro blue.
    gradient: 'from-teal-600 to-teal-800',
    terms: BUSINESS_TERMS
  }
};

/**
 * Look up a display term for a brand, falling back to the trade wording.
 *
 * The fallback is TRADE_TERMS rather than empty: an unknown brand code resolves to
 * TradesMatePro everywhere else in this file, and a term that renders as `undefined`
 * in a heading is worse than one that is merely wrong for one brand.
 */
export function getBrandTerm(brandCode, key) {
  const config = getBrandConfig(brandCode);
  return (config.terms || TRADE_TERMS)[key] || TRADE_TERMS[key] || '';
}

/**
 * The current brand's term, for callers that are NOT React components.
 *
 * Needed because several consumers are hooks (useDashboardData, useEmployeesPageData) or
 * render inside a `.map(t => ...)` where `t` is already bound to the row — so useBrand()'s
 * `t` helper is either illegal or shadowed there. Reads the same company-brand cache
 * BrandContext populates at login.
 */
export function currentTerm(key) {
  return getBrandTerm(getCurrentBrand()?.code, key);
}

/**
 * Get brand configuration by brand code
 */
export function getBrandConfig(brandCode) {
  return BRAND_CONFIG[brandCode] || BRAND_CONFIG.tradesmate;
}

/**
 * PLATFORM BRAND UTILITIES (Marketplace/Public - No Auth Required)
 * Use these ONLY when no company context exists
 */

export function getPlatformBrand() {
  const brandCode = resolveBrandFromHostname();
  return getBrandConfig(brandCode);
}

export function getPlatformBrandName() {
  return getPlatformBrand().name;
}

/**
 * COMPANY BRAND UTILITIES (Transactional - Source of Truth)
 * Use these when company context exists
 * RULE: Hostname MUST be ignored once company exists
 */

let companyBrandCache = null;
let companyContextExists = false;

export function setCompanyBrand(brandCode) {
  companyBrandCache = brandCode;
  companyContextExists = true;
}

export function getCompanyBrand() {
  if (!companyBrandCache || !companyContextExists) {
    return getPlatformBrand(); // Fallback only
  }
  return getBrandConfig(companyBrandCache);
}

export function getCompanyBrandName() {
  return getCompanyBrand().name;
}

/**
 * SMART BRAND RESOLUTION (Context-Aware)
 * Automatically chooses platform vs company brand based on context
 */

export function getCurrentBrand() {
  if (companyContextExists && companyBrandCache) {
    // Company context exists - use company brand (ignore hostname)
    return getCompanyBrand();
  } else {
    // No company context - use platform brand (from hostname)
    return getPlatformBrand();
  }
}

export function getCurrentBrandName() {
  return getCurrentBrand().name;
}

/**
 * COMPANY BRAND LOADING (Call after user login)
 * This is the ONLY handoff point from platform to company branding
 */

export async function loadCompanyBrand(supabase, companyId) {
  try {
    const { data: company, error } = await supabase
      .from('companies')
      .select('brand')
      .eq('id', companyId)
      .single();
    
    if (error) {
      console.error('Failed to load company brand:', error);
      return null;
    }
    
    const brandCode = company?.brand || 'tradesmate';
    setCompanyBrand(brandCode);
    return brandCode;
  } catch (error) {
    console.error('Error loading company brand:', error);
    return null;
  }
}

/**
 * CUSTOMER PORTAL BRAND UTILITIES
 * Resolve brand from: token → company_id → companies.brand
 * NEVER use hostname for customer portal
 */

export async function loadPortalBrand(supabase, portalToken) {
  try {
    // Get work order from portal token, then company brand
    const { data: workOrder, error } = await supabase
      .from('work_orders')
      .select('company_id, companies!work_orders_company_id_fkey(brand)')
      .eq('portal_token', portalToken)
      .single();
    
    if (error || !workOrder) {
      console.error('Failed to load portal brand:', error);
      return 'tradesmate'; // Fallback
    }
    
    const brandCode = workOrder.companies.brand || 'tradesmate';
    return brandCode;
  } catch (error) {
    console.error('Error loading portal brand:', error);
    return 'tradesmate'; // Fallback
  }
}

/**
 * COMPANY CREATION (Critical Handoff Point)
 * When creating a company, capture platform brand and store it
 */

export function captureCompanyBrandFromPlatform() {
  const platformBrand = resolveBrandFromHostname();
  return platformBrand;
}

/**
 * UTILITY FUNCTIONS
 */

// Compare on `code`, never on the DISPLAY NAME.
//
// These used to test `brand.name === 'SmbMate'`, which made a user-facing label
// load-bearing: renaming the brand, or writing it with different capitalisation in one
// of the four places it was defined, silently turned the check false and the brand
// stopped being detected. `code` is the internal identifier — it is what
// companies.brand stores and what BRAND_CONFIG is keyed by — so it is the only stable
// thing to branch on. The old version also read `brand.name` on a value it had just
// established might be a string, which is undefined for the string case.
// 'tmp' is the whitelabels.id for TradesMatePro (there is no 'tradesmate' row), and
// BrandService already aliases it. Normalising it here too means a raw companies.brand
// value resolves the same way whichever layer receives it — otherwise isTradesMatePro('tmp')
// is false, which is the exact silent-false failure this rewrite is removing.
const BRAND_CODE_ALIASES = {
  tmp: 'tradesmate',
  tradesmatepro: 'tradesmate'
};

function brandCodeOf(brandOrCode) {
  if (!brandOrCode) return null;
  const raw = typeof brandOrCode === 'string'
    ? brandOrCode.toLowerCase().trim()
    : brandOrCode.code || null;
  if (!raw) return null;
  return BRAND_CODE_ALIASES[raw] || raw;
}

export function isSmbMate(brandCode = null) {
  return brandCodeOf(brandCode || getCurrentBrand()) === 'smbmate';
}

export function isTradesMatePro(brandCode = null) {
  return brandCodeOf(brandCode || getCurrentBrand()) === 'tradesmate';
}

/**
 * EMAIL BRANDING (For Edge Functions)
 * Always resolves via: record → company_id → companies.brand
 * NEVER decides brand from email headers or hostname
 */

export function getEmailBranding(brandCode) {
  const config = getBrandConfig(brandCode);
  return {
    fromName: config.name,
    fromEmail: `noreply@${config.domain}`,
    supportEmail: config.supportEmail,
    footerText: `Powered by ${config.name}`,
    logoUrl: `https://${config.domain}${config.logo}`
  };
}

/**
 * RESET CACHE (for user logout or company switching)
 */

export function resetBrandCache() {
  companyBrandCache = null;
  companyContextExists = false;
}

/**
 * DEBUG UTILITIES
 */

export function getBrandingDebugInfo() {
  return {
    companyContextExists,
    companyBrandCache,
    platformBrand: resolveBrandFromHostname(),
    currentBrand: getCurrentBrand().name,
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'SSR'
  };
}
